import { createFileRoute, Link, Outlet, useRouter } from "@tanstack/react-router";
import { LayoutDashboard, ShoppingBag, Tags, FolderHeart, ClipboardList, LogOut, Home, Settings, Loader2, ShieldAlert, Ticket, Menu, X, Users } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useState } from "react";

export const Route = createFileRoute("/admin")({
  component: AdminLayout,
});

function AdminLayout() {
  const router = useRouter();
  const { user, role, loading, signOut } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Sidebar link items
  const menuItems = [
    { label: "Dashboard", to: "/admin", icon: LayoutDashboard, exact: true },
    { label: "Products", to: "/admin/products", icon: ShoppingBag },
    { label: "Categories", to: "/admin/categories", icon: Tags },
    { label: "Collections", to: "/admin/collections", icon: FolderHeart },
    { label: "Coupons", to: "/admin/coupons", icon: Ticket },
    { label: "Orders", to: "/admin/orders", icon: ClipboardList },
    { label: "Users & Admins", to: "/admin/users", icon: Users },
    { label: "Settings", to: "/admin/settings", icon: Settings },
  ];

  const handleLogout = () => {
    signOut();
    router.navigate({ to: "/auth", search: { redirect: undefined } });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center text-primary">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-gold" />
          <p className="eyebrow">Checking credentials...</p>
        </div>
      </div>
    );
  }

  if (!user || role !== "admin") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center text-primary px-6">
        <div className="max-w-md w-full bg-card border border-border rounded-[24px] p-8 text-center shadow-luxe space-y-6">
          <div className="h-16 w-16 bg-gold/10 border border-gold/30 rounded-full flex items-center justify-center mx-auto">
            <ShieldAlert className="h-8 w-8 text-gold" />
          </div>
          <div>
            <h2 className="font-display text-3xl font-bold">Access Denied</h2>
            <p className="text-sm text-muted-foreground mt-2">
              Only authorized members of the atelier can access this portal.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <Link to="/auth" search={{ redirect: undefined }} className="btn-gold justify-center">
              Sign In as Admin
            </Link>
            <Link to="/" className="btn-outline-dark justify-center">
              Back to Storefront
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-primary font-sans relative">
      
      {/* Desktop Fixed Sidebar */}
      <aside className="hidden lg:flex fixed top-0 left-0 bottom-0 w-64 bg-primary text-cream border-r border-border/20 flex-col shrink-0 h-screen z-40">
        {/* Brand logo/header */}
        <div className="h-20 border-b border-border/20 flex items-center px-6 shrink-0">
          <Link to="/" className="font-display text-2xl tracking-widest text-gold uppercase hover:opacity-80 transition-opacity font-bold">
            Royal Admin
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-6 px-4 space-y-2 overflow-y-auto">
          {menuItems.map((item) => (
            <Link
              key={item.label}
              to={item.to}
              activeOptions={{ exact: item.exact }}
              activeProps={{ className: "bg-gold text-primary font-semibold shadow-md" }}
              inactiveProps={{ className: "text-cream/70 hover:bg-card/10 hover:text-white transition-colors" }}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium uppercase tracking-wider"
            >
              <item.icon className="h-5 w-5" />
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-border/20 flex flex-col gap-2 shrink-0">
          <Link
            to="/"
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm hover:bg-card/10 hover:text-white transition-colors uppercase tracking-wider text-cream/70"
          >
            <Home className="h-5 w-5" />
            <span>Storefront</span>
          </Link>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-rose-400 hover:bg-rose-950/20 hover:text-rose-300 transition-colors uppercase tracking-wider text-left font-medium"
          >
            <LogOut className="h-5 w-5" />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Mobile Overlay Navigation Drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-[999] flex">
          {/* Backdrop */}
          <div
            onClick={() => setMobileOpen(false)}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm transition-opacity"
          />

          {/* Drawer Content */}
          <div className="relative w-4/5 max-w-xs bg-primary text-cream h-full flex flex-col shadow-2xl z-10">
            <div className="h-20 border-b border-border/20 flex items-center justify-between px-6 shrink-0">
              <Link to="/" className="font-display text-xl tracking-widest text-gold uppercase font-bold">
                Royal Admin
              </Link>
              <button
                onClick={() => setMobileOpen(false)}
                className="h-9 w-9 rounded-full bg-card/20 text-cream grid place-items-center"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <nav className="flex-1 py-6 px-4 space-y-2 overflow-y-auto">
              {menuItems.map((item) => (
                <Link
                  key={item.label}
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  activeOptions={{ exact: item.exact }}
                  activeProps={{ className: "bg-gold text-primary font-semibold shadow-md" }}
                  inactiveProps={{ className: "text-cream/70 hover:bg-card/10 hover:text-white transition-colors" }}
                  className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-medium uppercase tracking-wider"
                >
                  <item.icon className="h-5 w-5" />
                  <span>{item.label}</span>
                </Link>
              ))}
            </nav>

            <div className="p-4 border-t border-border/20 flex flex-col gap-2 shrink-0">
              <Link
                to="/"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm hover:bg-card/10 hover:text-white transition-colors uppercase tracking-wider text-cream/70"
              >
                <Home className="h-5 w-5" />
                <span>Storefront</span>
              </Link>
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-rose-400 hover:bg-rose-950/20 hover:text-rose-300 transition-colors uppercase tracking-wider text-left font-medium"
              >
                <LogOut className="h-5 w-5" />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area (Shifted by pl-64 on desktop) */}
      <div className="lg:pl-64 flex flex-col min-h-screen w-full">
        {/* Sticky Top Header */}
        <header className="h-16 sm:h-20 bg-card/95 backdrop-blur-md border-b border-border flex items-center justify-between px-4 sm:px-8 shrink-0 shadow-sm sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileOpen(true)}
              className="lg:hidden h-10 w-10 rounded-full bg-secondary border border-border grid place-items-center text-primary hover:text-gold"
            >
              <Menu className="h-5 w-5" />
            </button>
            <span className="eyebrow text-xs sm:text-sm">Atelier Management</span>
          </div>

          <div className="flex items-center gap-3">
            <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center font-display text-base sm:text-lg text-gold font-bold">
              A
            </div>
            <div className="hidden sm:block text-left">
              <div className="text-xs uppercase tracking-wider font-semibold text-primary">Royal Admin</div>
              <div className="text-[0.65rem] tracking-wider text-muted-foreground uppercase">Atelier Overseer</div>
            </div>
          </div>
        </header>

        {/* Content Outlet */}
        <main className="flex-1 p-4 sm:p-8 max-w-[1600px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
